import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { PageHeader } from '@/components/kit/Layout';
import { Modal } from '@/components/kit/Modal';
import { Alert, EmptyState, Note, Skeleton } from '@/components/kit/Note';
import { Panel, Stack } from '@/components/kit/Panel';
import { useToast } from '@/components/kit/Toast';

/**
 * Демонстрационный экран состояний.
 *
 * Показывает все три обязательных состояния — пусто, загрузка
 * скелетом, ошибка — плюс предупреждения и необратимое действие.
 * Нужен не пользователю, а сдаче: по нему сверяют, что состояния
 * выглядят одинаково везде, а не изобретаются на каждом экране
 * заново (ТЗ §7).
 */
export function StatesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);

  const skeletonRow = (width: number, key: number) => (
    <tr key={key}>
      <td data-col="primary">
        <Skeleton width={`${width}%`} />
      </td>
      <td>
        <Skeleton width="45%" />
      </td>
      <td data-col="secondary">
        <div className="skel" style={{ width: 52, height: 14, marginLeft: 'auto' }} />
      </td>
    </tr>
  );

  return (
    <div className="main">
      <PageHeader title="Состояния" />

      <Stack>
        <Panel title="Пусто">
          <EmptyState>
            В сезоне ещё нет мероприятий.
            <br />
            <TextLink onClick={() => navigate('/events/new')}>Создать первое</TextLink>
          </EmptyState>
        </Panel>

        <Panel title="Требует действия">
          <Stack>
            <Alert
              action={
                <Button small tone="quiet" onClick={() => toast.show('Здесь открылось бы окно публикации')}>
                  Что осталось
                </Button>
              }
            >
              <b>Готово 9 из 12 проверок к публикации.</b>
              <span>Пока мероприятие в черновике, отдел продаж его не видит</span>
            </Alert>
            <Alert
              action={
                <Button small tone="quiet" onClick={() => toast.show('Здесь открылась бы карточка пакета')}>
                  Открыть пакет
                </Button>
              }
            >
              <b>BEGINNER PASS: осталось 83 продажи до повышения цены.</b>
              <span>Порог сработает раньше даты</span>
            </Alert>
          </Stack>
        </Panel>

        <Panel title="Наблюдение">
          <Note>
            <b>Сумма лимитов — 226 команд.</b> В Иркутске 2026 приехало 157. Проверьте лимиты,
            прежде чем публиковать
          </Note>
        </Panel>

        <Panel title="Ошибка">
          <Alert tone="crit">
            <b>Не удалось сохранить состав пакета.</b>
            <span>Проверьте соединение и повторите.</span>
          </Alert>
        </Panel>

        <Panel title="Загрузка" flush>
          <table className="tbl">
            <thead>
              <tr>
                <th>Город</th>
                <th>Состояние</th>
                <th className="r">Лимит</th>
              </tr>
            </thead>
            <tbody>{[52, 64, 44].map(skeletonRow)}</tbody>
          </table>
        </Panel>

        <Panel title="Необратимое действие">
          <Button tone="danger" onClick={() => setConfirm(true)}>
            Удалить мероприятие…
          </Button>
        </Panel>
      </Stack>

      {confirm ? (
        <Modal
          title="Удалить мероприятие?"
          onClose={() => setConfirm(false)}
          confirm={{
            label: 'Удалить',
            danger: true,
            onClick: () => {
              setConfirm(false);
              toast.show('Здесь мероприятие было бы удалено');
            },
          }}
        >
          <p className="txt">
            Удалится вместе со всем содержимым: номинациями, услугами, программой, пакетами.
            Действие необратимо.
          </p>
          <Note tone="crit">Удалить можно только черновик — опубликованное остаётся в системе</Note>
        </Modal>
      ) : null}
    </div>
  );
}
